import { useCurrency } from '@/contexts/CurrencyContext'
import { useFiscalStatus } from '@/hooks/useFiscalStatus'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowDownRight,
  ArrowUpRight,
  Landmark,
  Minus,
  Receipt,
  RotateCcw,
  Scale,
  ShoppingCart,
  TrendingDown,
  Wallet,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { DashboardOverview, IntMetricPair, MetricPair } from '@/types'
import {
  compactCurrency,
  formatCount,
  formatTrendPercent,
  metricPairOrEmpty,
  trendDirection,
  type TrendDirection,
} from './dashboardFormat'
import { cn } from '@/lib/utils'
import { MetricLabel } from '@/components/finance/MetricLabel'
import type { MetricKey } from '@/lib/financeGlossary'

interface KpiHeroRowProps {
  overview?: DashboardOverview
  isLoading: boolean
  previousLabel?: string
}

// Headline metric is Gross Sales — the tax-inclusive number a manager
// uses to reconcile the till. Net Sales lives in the P&L panel below.
export function KpiHeroRow({ overview, isLoading, previousLabel }: KpiHeroRowProps) {
  const { formatCurrency, currencyCode } = useCurrency()
  const { enabled: fiscalEnabled } = useFiscalStatus()

  if (isLoading || !overview) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {Array.from({ length: fiscalEnabled ? 7 : 6 }).map((_, i) => (
          <KpiSkeleton key={i} />
        ))}
      </div>
    )
  }

  const refunds = metricPairOrEmpty(overview.refunds_issued)
  const grossAfterRefunds = metricPairOrEmpty(overview.gross_sales_after_refunds)
  const netSalesTax = metricPairOrEmpty(overview.net_sales_tax)
  const netProfitAfterRefunds = metricPairOrEmpty(overview.net_profit_after_refunds)
  // F-RPT-N1: surface the refund-adjusted Net Profit tile only when
  // refunds are non-zero in either window. The two numbers are equal
  // when there are no refunds, so showing both at all times is just
  // noise. The threshold matches the >Rs 0.01 floor we already use in
  // metricPairOrEmpty for "is this number meaningful?" decisions.
  const hasRefundActivity = refunds.current > 0.005 || refunds.previous > 0.005

  const tiles: KpiTileProps[] = [
    {
      label: 'Gross sales',
      metric: 'gross_sales',
      icon: Wallet,
      iconClassName: 'text-emerald-600',
      value: compactCurrency(overview.gross_sales.current, currencyCode),
      tooltip: formatCurrency(overview.gross_sales.current),
      previous: formatCurrency(overview.gross_sales.previous),
      pair: overview.gross_sales,
      polarity: 'positive',
    },
    {
      label: 'Orders',
      metric: 'orders',
      icon: ShoppingCart,
      iconClassName: 'text-blue-600',
      value: formatCount(overview.orders.current),
      tooltip: `${formatCount(overview.orders.current)} completed (out of ${formatCount(
        overview.orders_placed.current,
      )} placed)`,
      previous: `${formatCount(overview.orders.previous)} prior`,
      pair: overview.orders,
      polarity: 'positive',
    },
    {
      label: 'Avg order value',
      metric: 'avg_ticket',
      icon: Receipt,
      iconClassName: 'text-violet-600',
      value: compactCurrency(overview.avg_ticket.current, currencyCode),
      tooltip: formatCurrency(overview.avg_ticket.current),
      previous: formatCurrency(overview.avg_ticket.previous),
      pair: overview.avg_ticket,
      polarity: 'positive',
    },
    {
      label: 'Refunds issued',
      metric: 'refunds_issued',
      icon: RotateCcw,
      iconClassName: 'text-rose-600',
      value: compactCurrency(refunds.current, currencyCode),
      tooltip: formatCurrency(refunds.current),
      previous: formatCurrency(refunds.previous),
      pair: refunds,
      polarity: 'negative',
    },
    {
      label: 'Gross sales after refunds',
      metric: 'gross_sales_after_refunds',
      icon: Scale,
      iconClassName: 'text-teal-600',
      value: compactCurrency(grossAfterRefunds.current, currencyCode),
      tooltip: formatCurrency(grossAfterRefunds.current),
      previous: formatCurrency(grossAfterRefunds.previous),
      pair: grossAfterRefunds,
      polarity: 'positive',
    },
  ]

  // F-RPT-N1: Net profit (after refunds) — appended conditionally so
  // it only takes a slot when the period actually has refunds. On a
  // clean day the two profit numbers are identical and showing both
  // is just visual noise. When refunds exist, this tile carries the
  // operator's *real* end-of-day economic position.
  if (hasRefundActivity) {
    tiles.push({
      label: 'Net profit (after refunds)',
      metric: 'net_profit_after_refunds',
      icon: TrendingDown,
      iconClassName: 'text-rose-600',
      value: compactCurrency(netProfitAfterRefunds.current, currencyCode),
      tooltip: `${formatCurrency(netProfitAfterRefunds.current)} — Net profit minus refunds in the same window`,
      previous: formatCurrency(netProfitAfterRefunds.previous),
      pair: netProfitAfterRefunds,
      polarity: 'positive',
      footnote: `Net profit ${formatCurrency(overview.net_profit.current)} − refunds ${formatCurrency(refunds.current)}`,
    })
  }

  // Sales tax tile — output VAT net of FBR credit notes, the figure to
  // compare against your PRA monthly portal filing. Only relevant for
  // tenants with a configured fiscal jurisdiction; non-fiscal tenants have
  // no PRA liability so the tile would always read zero.
  if (fiscalEnabled) {
    tiles.push({
      label: 'Sales tax (net)',
      metric: 'net_sales_tax',
      icon: Landmark,
      iconClassName: 'text-indigo-600',
      value: compactCurrency(netSalesTax.current, currencyCode),
      tooltip: `${formatCurrency(netSalesTax.current)} — net of credit-note reversals`,
      previous: formatCurrency(netSalesTax.previous),
      pair: netSalesTax,
      polarity: 'positive',
    })
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
      {tiles.map((t) => (
        <KpiTile key={t.label} {...t} previousLabel={previousLabel} />
      ))}
    </div>
  )
}

interface KpiTileProps {
  label: string
  /** Glossary key drives the tooltip on the label itself. */
  metric: MetricKey
  icon: LucideIcon
  iconClassName?: string
  value: string
  /** Full-precision value shown on hover. */
  tooltip?: string
  previous: string
  pair: MetricPair | IntMetricPair
  polarity?: 'positive' | 'negative'
  previousLabel?: string
  /** Optional one-liner under the trend badge (e.g. net order count). */
  footnote?: string
}

function KpiTile({ label, metric, icon: Icon, iconClassName, value, tooltip, previous, pair, polarity = 'positive', previousLabel, footnote }: KpiTileProps) {
  const dir = trendDirection(pair.pct, polarity)
  const pctText = formatTrendPercent(pair.pct)
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <MetricLabel
            metric={metric}
            label={label}
            className="text-sm font-medium text-muted-foreground"
          />
          <Icon className={cn('h-4 w-4', iconClassName)} />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-3xl font-semibold leading-none tracking-tight" title={tooltip}>
            {value}
          </span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendBadge direction={dir} text={pctText ?? '—'} />
            <span className="truncate">
              vs {previous}
              {previousLabel ? ` (${previousLabel})` : ''}
            </span>
          </div>
          {footnote ? (
            <span className="text-[11px] text-muted-foreground">{footnote}</span>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

const TREND_STYLES: Record<TrendDirection, { className: string; Icon: LucideIcon }> = {
  up: {
    className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300',
    Icon: ArrowUpRight,
  },
  down: {
    className: 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300',
    Icon: ArrowDownRight,
  },
  flat: {
    className: 'border-muted-foreground/20 bg-muted text-muted-foreground',
    Icon: Minus,
  },
  unknown: {
    className: 'border-muted-foreground/20 bg-muted text-muted-foreground',
    Icon: Minus,
  },
}

function TrendBadge({ direction, text }: { direction: TrendDirection; text: string }) {
  const { className, Icon } = TREND_STYLES[direction]
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] font-semibold', className)}>
      <Icon className="h-3 w-3" />
      {text}
    </span>
  )
}

function KpiSkeleton() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-4 rounded" />
        </div>
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  )
}
